import env from '../constants/env'

export type PortOnePayResponse = {
    // 신 SDK(v1/iamport.js) + 신모듈 PG는 콜백에 success 필드를 주지 않음 (아래 requestPay에서 정규화)
    success?: boolean
    imp_uid: string | null
    merchant_uid: string
    error_code?: string
    error_msg?: string
    // 그 외 iamport 응답 필드(paid_amount, card_name 등)
    [key: string]: unknown
}

export type RequestPayParams = {
    merchant_uid: string
    amount: number
    name: string
    buyer_email?: string
    buyer_name?: string
    buyer_tel?: string
    language?: string
    /** 모바일 결제 후 돌아올 주소. 질의값으로 imp_uid · merchant_uid · error_msg 가 붙는다 */
    m_redirect_url?: string
}

/**
 * 단건(크레딧 팩) 결제 상점이 설정돼 있는가.
 *
 * 단건 채널키 · 단건 채널이 모두 없으면 구매를 막는다. 정기결제 상점(env.PG)으로 대체하면
 * 반복 청구 전용 계약 위반이라(정책 7절 · 명세 5-6) 같은 값을 넣은 설정도 미설정으로 본다.
 */
export function isOneTimePayConfigured() {
    if (env.PG_ONETIME_CHANNEL_KEY) return true
    return Boolean(env.PG_ONETIME) && env.PG_ONETIME !== env.PG
}

/**
 * 레거시 iamport(window.IMP) 단건결제(일반결제/인증결제) 호출 래퍼.
 * 빌링키 발급과 달리 customer_uid를 넣지 않는다.
 * IMP.request_pay 콜백을 Promise로 감싼다.
 */
export function requestPay(params: RequestPayParams): Promise<PortOnePayResponse> {
    return new Promise((resolve) => {
        // @ts-ignore - 레거시 SDK는 window 전역으로 주입됨 (public/index.html)
        const {IMP} = window
        if (!IMP) {
            resolve({
                success: false,
                imp_uid: null,
                merchant_uid: params.merchant_uid,
                error_msg: 'IMP SDK가 로드되지 않았습니다.',
            })
            return
        }

        // 채널키가 있으면 채널키로 채널을 명시(신 콘솔 다중 채널 라우팅 이슈 회피),
        // 없으면 레거시 pg 문자열(env.PG_ONETIME) 사용.
        const channelParam = env.PG_ONETIME_CHANNEL_KEY
            ? {channelKey: env.PG_ONETIME_CHANNEL_KEY}
            : {pg: env.PG_ONETIME}

        if (!isOneTimePayConfigured()) {
            resolve({
                success: false,
                imp_uid: null,
                merchant_uid: params.merchant_uid,
                error_msg: '단건 결제 설정이 없어 결제할 수 없습니다.',
            })
            return
        }

        IMP.init(env.IMP)
        IMP.request_pay(
            {
                ...channelParam,
                pay_method: 'card',
                // popup: true 금지 — 인증결제는 팝업 내 카드사 3DS 리다이렉트로 opener가 끊겨
                // 콜백이 [NO_CODE] 빈 실패로 유실됨. 기본(iframe) 모드로 같은 창에서 처리한다.
                // 단건 웹훅은 BE의 /webhook/portone 로 받는다. env.API_HOST 기반이라
                // dev 결제→dev BE, prod 결제→prod BE 로 결정적으로 라우팅됨(콘솔 설정과 무관).
                notice_url: `${env.API_HOST}/webhook/portone`,
                ...params,
            },
            (resp: PortOnePayResponse) => {
                // 신 SDK + 신모듈 PG(신 토스페이먼츠 등) 콜백은 imp_uid/merchant_uid/error_code/error_msg
                // 4개 필드만 반환하고 success 필드가 없다 → error_code 부재 + imp_uid 존재를 성공으로 정규화.
                const success = resp.success ?? (!resp.error_code && Boolean(resp.imp_uid))
                resolve({...resp, success})
            },
        )
    })
}
